import { forwardRef, useState, useCallback } from 'react';
import type { MouseEvent } from 'react';

import type { Action } from '../../types';
import { useConsolidatedRef, useI18n } from '../../hooks';
import Actions from '../Actions';
import type { MenuItemProps } from '../Menu/Menu.types';
import MenuButton from '../MenuButton';

import type { SelectionCardProps } from './SelectionCard.types';

interface SelectionCardActionsProps extends Pick<SelectionCardProps, 'disabled'> {
  actions: NonNullable<SelectionCardProps['actions']>;
  contextualLabel?: string;
}

const SelectionCardActions = forwardRef<HTMLButtonElement, SelectionCardActionsProps>(
  function SelectionCardActions({ actions, disabled = false, contextualLabel, ...restProps }, ref) {
    const t = useI18n();
    const menuButtonRef = useConsolidatedRef(ref);

    const [isLoading, setIsLoading] = useState(false);
    const [menuItems, setMenuItems] = useState<MenuItemProps[]>([]);

    const mapActions = useCallback(
      (fetchedActions: Action[]): MenuItemProps[] =>
        fetchedActions.map(({ text, onClick, ...action }) => ({
          ...action,
          primary: text,
          onClick: onClick
            ? (id, e) => {
                onClick(id, e, menuButtonRef.current ?? undefined);
                menuButtonRef.current?.focus();
              }
            : undefined
        })),
      []
    );

    const handleClick = useCallback(
      async (e: MouseEvent<HTMLButtonElement>) => {
        if (typeof actions !== 'function') return;
        if (e.currentTarget.getAttribute('aria-expanded') === 'true') return;

        setIsLoading(true);
        setMenuItems([]);

        try {
          const fetchedActions = await actions();
          setMenuItems(mapActions(fetchedActions));
        } catch {
          // rejection handled silently
        } finally {
          setIsLoading(false);
        }
      },
      [actions, mapActions]
    );

    if (typeof actions === 'function') {
      return (
        <MenuButton
          {...restProps}
          ref={menuButtonRef}
          text={t('actions')}
          aria-label={contextualLabel ? t('actions_for', [contextualLabel]) : undefined}
          iconOnly
          icon='more'
          variant='simple'
          disabled={disabled}
          onClick={handleClick}
          menu={{
            items: menuItems,
            loading: isLoading
          }}
        />
      );
    }

    return (
      <Actions
        {...restProps}
        items={actions}
        menuAt={1}
        disabled={disabled}
        contextualLabel={contextualLabel}
      />
    );
  }
);

export default SelectionCardActions;
